import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter,
  DialogDescription
} from './ui/dialog';
import { toast } from 'sonner';
import { Settings, Globe, MapPin, User } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UserSettings = ({ open, onClose }) => {
  const { token, user, updateUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [timezones, setTimezones] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    timezone: 'UTC',
    location: ''
  });

  useEffect(() => {
    if (open && user) {
      setFormData({
        name: user.name || '',
        timezone: user.timezone || 'UTC',
        location: user.location || ''
      });
      fetchTimezones();
    }
  }, [open, user]);

  const fetchTimezones = async () => {
    try {
      const response = await fetch(`${API}/timezones`);
      if (response.ok) {
        const data = await response.json();
        setTimezones(data.timezones || []);
      }
    } catch (error) {
      console.error('Error fetching timezones:', error);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/users/me/settings`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const updatedUser = await response.json();
        if (updateUser) {
          updateUser(updatedUser);
        }
        toast.success('Settings updated successfully');
        onClose();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update settings');
      }
    } catch (error) {
      toast.error('Failed to update settings');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            User Settings
          </DialogTitle>
          <DialogDescription>
            Update your profile and timezone preferences for report scheduling
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="name" className="flex items-center gap-2">
              <User className="w-4 h-4" />
              Display Name
            </Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Your name"
              data-testid="settings-name-input"
            />
          </div>

          {/* Timezone */}
          <div className="space-y-2">
            <Label htmlFor="timezone" className="flex items-center gap-2">
              <Globe className="w-4 h-4" />
              Timezone
            </Label>
            <Select
              value={formData.timezone}
              onValueChange={(value) => setFormData({ ...formData, timezone: value })}
            >
              <SelectTrigger data-testid="settings-timezone-select">
                <SelectValue placeholder="Select your timezone" />
              </SelectTrigger>
              <SelectContent>
                {timezones.map((tz) => (
                  <SelectItem key={tz.value} value={tz.value}>
                    {tz.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Daily reports will be sent at 9 AM and 3 PM in your local time
            </p>
          </div>

          {/* Location */}
          <div className="space-y-2">
            <Label htmlFor="location" className="flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              Location (Optional)
            </Label>
            <Input
              id="location"
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              placeholder="e.g., New York, USA"
              data-testid="settings-location-input"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="settings-cancel-btn">
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={loading} data-testid="settings-save-btn">
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UserSettings;
